import React, { useState, useEffect } from 'react';
import { Advisor, CurriculumModule, EvaluationQuestion, EvaluationResult } from '../types';
import { api } from '../services/api';
import { ArrowLeftIcon, Spinner, CheckCircleIcon, DownloadIcon } from '../components/icons';
import jsPDF from 'jspdf';

interface EvaluationPageProps {
  module?: CurriculumModule;
  isFinal: boolean;
  user: Advisor;
  onExit: () => void;
}

// Helper function to remove accents for jsPDF compatibility with standard fonts
const normalizeText = (text: string): string => {
    if (!text) return '';
    // This will remove accents and other diacritical marks.
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

const QuestionTracker: React.FC<{
  count: number;
  currentIndex: number;
  answers: (number | null)[];
  onJumpTo: (index: number) => void;
}> = ({ count, currentIndex, answers, onJumpTo }) => {
  return (
    <div className="flex flex-wrap justify-center gap-2 mb-8 border-b border-slate-700 pb-4">
      {Array.from({ length: count }).map((_, index) => {
        const isAnswered = answers[index] !== null;
        const isCurrent = index === currentIndex;
        let bgColor = 'bg-slate-600 hover:bg-slate-500';
        if (isCurrent) {
          bgColor = 'bg-blue-600 ring-2 ring-blue-400';
        } else if (isAnswered) {
          bgColor = 'bg-green-700 hover:bg-green-600';
        }
        return (
          <button
            key={index}
            onClick={() => onJumpTo(index)}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm transition-colors ${bgColor}`}
            aria-label={`Ir a la pregunta ${index + 1}`}
          >
            {index + 1}
          </button>
        );
      })}
    </div>
  );
};


const EvaluationPage: React.FC<EvaluationPageProps> = ({ module, isFinal, user, onExit }) => {
  const [questions, setQuestions] = useState<EvaluationQuestion[]>([]);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [result, setResult] = useState<EvaluationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isGeneratingCert, setIsGeneratingCert] = useState(false);
  const [confirmingSubmit, setConfirmingSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFinished = !!result;

  const shuffleArray = <T,>(array: T[]): T[] => {
    return [...array].sort(() => Math.random() - 0.5);
  };

  useEffect(() => {
    const evaluationId = isFinal ? 'final-bootcamp' : module!.id;
    const existingResult = user.progress[`eval-${evaluationId}`] as EvaluationResult;
    
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const allQuestions = isFinal 
            ? await api.getAllQuestions() 
            : await api.getQuestionsForModule(module!.id);
            
        const selectedQuestions = shuffleArray(allQuestions).slice(0, 20); // Limit to 20 questions for any eval
        if (selectedQuestions.length === 0) {
            setError("No hay preguntas disponibles para esta evaluación.");
        } else {
            setQuestions(selectedQuestions);
            if (!existingResult) { // Only reset answers if it's a new attempt
                setAnswers(new Array(selectedQuestions.length).fill(null));
            }
        }
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar la evaluación.');
        setLoading(false);
      }
    };

    if (existingResult) {
        // To show detailed review, we need questions. Let's fetch them anyway.
        // We assume the questions don't change often. For a real-world app, we might need to store questions taken.
        fetchQuestions();
        setResult(existingResult);
    } else {
        fetchQuestions();
    }
  }, [module, isFinal, user.progress]);

  const handleAnswerSelect = (optionIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = optionIndex;
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      // FIX: The previous button was incorrectly advancing the question index.
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleJumpToQuestion = (index: number) => {
    setCurrentQuestionIndex(index);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSaveError(null);

    let correctCount = 0;
    questions.forEach((q, index) => {
        if (q.correctAnswer === answers[index]) {
            correctCount++;
        }
    });
    const score = questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0;
    const passed = score >= 80;

    const newResult: EvaluationResult = { score, passed, date: Date.now() };

    try {
        const evaluationId = isFinal ? 'final-bootcamp' : module!.id;
        await api.saveEvaluationResult(user.id, evaluationId, newResult);
    } catch(err) {
        setSaveError("No se pudo guardar el resultado. Contacta al administrador. Tu calificación fue calculada, pero no se registró en tu progreso.");
    } finally {
        setResult(newResult);
    }
  };
  
    const handleDownloadCertificate = async () => {
        setIsGeneratingCert(true);
        try {
            const settings = await api.getCertificateSettings();
            if (!settings) {
                alert("La configuración del certificado no está disponible. Contacta al administrador.");
                setIsGeneratingCert(false);
                return;
            }

            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            // Background
            doc.setFillColor(settings.primaryColor || '#0f172a');
            doc.rect(0, 0, pageWidth, pageHeight, 'F');
            doc.setFillColor(settings.secondaryColor || '#4338ca');
            doc.rect(0, 0, pageWidth, 20, 'F');
            doc.rect(0, pageHeight - 20, pageWidth, 20, 'F');
            
            // Logo
            if (settings.logoUrl && settings.logoUrl.startsWith('data:image')) {
                try {
                    doc.addImage(settings.logoUrl, undefined, pageWidth / 2 - 15, 25, 30, 30);
                } catch(e) {
                    console.error("Error adding logo to certificate:", e);
                    // Continue without the logo
                }
            }

            // Main Text
            const completionDate = result?.date ? new Date(result.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
            
            const advisorName = user.name || 'Asesor';

            const defaultCertText = `Por la presente se certifica que [ADVISOR_NAME] ha completado con éxito APEX University el [COMPLETION_DATE].`;

            let mainText = (settings.text || defaultCertText)
                .replace(/\[ADVISOR_NAME\]/g, advisorName)
                .replace(/\[COMPLETION_DATE\]/g, completionDate);

            doc.setFontSize(28);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor('#FFFFFF');
            doc.text(normalizeText("CERTIFICADO DE FINALIZACIÓN"), pageWidth / 2, 70, { align: 'center' });
            
            doc.setFontSize(14);
            doc.setFont('helvetica', 'normal');
            doc.text(normalizeText(mainText), pageWidth / 2, 90, { align: 'center', maxWidth: 220 });
            
            // Signatories
            if (settings.signatories && settings.signatories.length > 0) {
                const signatoryCount = settings.signatories.length;
                const totalWidth = signatoryCount * 80;
                const startX = (pageWidth - totalWidth) / 2 + 10;
                
                settings.signatories.forEach((sig, index) => {
                    const x = startX + index * 80;
                    if (sig.signatureUrl && sig.signatureUrl.startsWith('data:image')) {
                        try {
                            doc.addImage(sig.signatureUrl, undefined, x, pageHeight - 65, 60, 20);
                        } catch (e) {
                            console.error(`Error adding signature for ${sig.name}:`, e);
                            // Continue without this signature
                        }
                    }
                    doc.setLineWidth(0.5);
                    doc.line(x, pageHeight - 40, x + 60, pageHeight - 40);
                    doc.setFontSize(10);
                    doc.text(normalizeText(sig.name || ''), x + 30, pageHeight - 35, { align: 'center' });
                    doc.setFontSize(8);
                    doc.text(normalizeText(sig.title || ''), x + 30, pageHeight - 30, { align: 'center' });
                });
            }
            
            const sanitizedName = normalizeText(advisorName)
                .replace(/\s+/g, '_')
                .replace(/[^a-zA-Z0-9_-]/g, '');

            doc.save(`Certificado_APEX_${sanitizedName || 'sin_nombre'}.pdf`);

        } catch (e) {
            console.error("Failed to generate certificate", e);
            alert("Hubo un error al generar el certificado. Por favor, inténtalo de nuevo.");
        } finally {
            setIsGeneratingCert(false);
        }
  };

  const allQuestionsAnswered = questions.length > 0 && answers.length === questions.length && answers.every(a => a !== null);
  const currentQuestion = questions[currentQuestionIndex];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex justify-center items-center text-white">
        <Spinner className="h-12 w-12" />
        <span className="ml-4 text-xl">Cargando Evaluación...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col justify-center items-center text-white p-4">
        <h2 className="text-2xl text-red-500 mb-4">Error</h2>
        <p className="text-center">{error}</p>
        <button onClick={onExit} className="mt-8 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
          Volver al Dashboard
        </button>
      </div>
    );
  }
  
  const renderResultScreen = () => {
      const isPassed = result!.passed;
      const canDownloadCertificate = isFinal && isPassed;
      return (
        <div className="min-h-screen bg-slate-900 flex justify-center items-center p-4">
            <div className="max-w-2xl w-full bg-slate-800 p-8 rounded-xl shadow-lg text-center text-white animate-fade-in border border-slate-700">
                <CheckCircleIcon className={`w-24 h-24 mx-auto ${isPassed ? 'text-green-500' : 'text-red-500'}`} />
                <h2 className="text-4xl font-bold mt-4">Evaluación Finalizada</h2>
                <p className={`text-2xl font-semibold mt-2 ${isPassed ? 'text-green-400' : 'text-red-400'}`}>
                    {isPassed ? '¡Felicidades, has aprobado!' : 'Necesitas seguir estudiando'}
                </p>
                <div className="my-8 bg-slate-700 p-6 rounded-lg">
                    <p className="text-lg text-gray-300">Tu calificación final es:</p>
                    <p className="text-6xl font-bold my-2">{result!.score}%</p>
                    <p className="text-sm text-gray-400">Se requiere un mínimo de 80% para aprobar.</p>
                </div>
                {saveError && (
                    <div className="bg-red-900 border border-red-700 text-red-200 p-3 rounded-md mb-4">
                        <p className="text-sm">{saveError}</p>
                    </div>
                )}
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                    {isPassed && !isFinal ? (
                        <>
                            <button onClick={onExit} className="w-full sm:w-auto bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 px-6 rounded-lg transition-colors">
                                Volver al Dashboard
                            </button>
                            <button onClick={onExit} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors">
                                Continuar al Siguiente Módulo
                            </button>
                        </>
                    ) : (
                        <button onClick={onExit} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors">
                            Volver al Dashboard
                        </button>
                    )}
                    
                    {canDownloadCertificate && (
                        <button 
                            onClick={handleDownloadCertificate}
                            disabled={isGeneratingCert}
                            className="w-full sm:w-auto flex items-center justify-center bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50"
                        >
                            {isGeneratingCert ? <Spinner className="h-5 w-5 mr-2" /> : <DownloadIcon className="h-5 w-5 mr-2" />}
                            {isGeneratingCert ? 'Generando...' : 'Descargar Certificado'}
                        </button>
                    )}
                </div>
            </div>
        </div>
      );
  };
  
  if (isFinished) {
      return renderResultScreen();
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <header className="mb-6">
          <button onClick={onExit} className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 mb-4">
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Volver al Dashboard</span>
          </button>
          <h1 className="text-3xl font-bold">
            {isFinal ? 'Evaluación Final' : `Evaluación: ${module!.title}`}
          </h1>
          <p className="text-gray-400 mt-1">
            Pregunta {currentQuestionIndex + 1} de {questions.length}
          </p>
        </header>

        <QuestionTracker
          count={questions.length}
          currentIndex={currentQuestionIndex}
          answers={answers}
          onJumpTo={handleJumpToQuestion}
        />

        {currentQuestion && (
          <div className="bg-slate-800 p-6 sm:p-8 rounded-lg shadow-lg border border-slate-700 animate-fade-in">
            <h2 className="text-xl sm:text-2xl font-semibold mb-6">{currentQuestion.question}</h2>
            <div className="space-y-4">
              {currentQuestion.options.map((option, index) => {
                const isSelected = answers[currentQuestionIndex] === index;
                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors duration-200 ${
                      isSelected
                        ? 'bg-blue-800 border-blue-500'
                        : 'bg-slate-700 border-slate-600 hover:bg-slate-600'
                    }`}
                  >
                    <span className="font-medium">{option}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <button
            onClick={handlePrev}
            disabled={currentQuestionIndex === 0 || confirmingSubmit || isSubmitting}
            className="w-full sm:w-auto bg-slate-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Anterior
          </button>

            <div className="w-full sm:w-auto order-first sm:order-none">
                {!confirmingSubmit ? (
                <button
                    onClick={() => setConfirmingSubmit(true)}
                    disabled={!allQuestionsAnswered}
                    className="w-full bg-green-600 text-white font-semibold py-3 px-8 rounded-lg hover:bg-green-700 disabled:bg-slate-500 disabled:cursor-not-allowed transition-colors text-lg"
                    title={!allQuestionsAnswered ? "Debes responder todas las preguntas para finalizar" : "Finalizar y ver resultados"}
                >
                    Finalizar Evaluación
                </button>
                ) : (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 p-2 rounded-lg bg-slate-800 border border-slate-700">
                    <span className="text-gray-300 font-semibold">¿Estás seguro?</span>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setConfirmingSubmit(false)} disabled={isSubmitting} className="w-full sm:w-auto bg-slate-600 hover:bg-slate-500 text-white font-semibold py-2 px-4 rounded-lg transition-colors">
                            Cancelar
                        </button>
                        <button onClick={handleSubmit} disabled={isSubmitting} className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center">
                            {isSubmitting ? <Spinner className="h-5 w-5 mr-2" /> : null}
                            {isSubmitting ? 'Enviando...' : 'Sí, finalizar'}
                        </button>
                    </div>
                </div>
                )}
            </div>

          <button
            onClick={handleNext}
            disabled={currentQuestionIndex === questions.length - 1 || confirmingSubmit || isSubmitting}
            className="w-full sm:w-auto bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
};

export default EvaluationPage;